import { routeHref } from '../../lib/router.js'
import { mailboxAddresses, mailboxName } from '../../lib/mail.js'
import { mailNotices, touched } from '../../lib/live.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- tpl-inbox ---
let __fez_component_0;
{
const FOLDERS = ['inbox', 'feed', 'papertrail', 'spam', 'starred', 'assigned', 'notes', 'snoozed', 'drafts', 'sent', 'archive', 'trash', 'all']
const JUMPS = { i: 'inbox', s: 'starred', d: 'drafts', t: 'sent', a: 'all' }
// How long the new mail of a mailbox gathers before it notifies, so a burst groups.
const BURST_MS = 2000
__fez_component_0 = class {
  PROPS = {
    container: String,
    mailbox: String,
    folder: String,
    addr: String,
    host: String,
    q: String,
    thread: String,
    email: String,
    box: String,
    label: String,
    source: String,
    unread: Boolean,
  }

  init() {
    this.state.loading = true
    this.state.error = ''
    this.state.containers = []
    this.state.suspended = []
    this.state.navOpen = false
    this.state.tick = 0
    this.state.mode = Fez.localStorage.get('mail-mode', 'threads')
    this.state.counts = {}
    this.state.labels = []
    this.state.sources = []
    this.state.forwards = []
    // New Inbox mail per mailbox waiting to notify (`BURST_MS`).
    this.state.bursts = {}
    this.state.notify = Fez.localStorage.get('mail-notify', false) && window.Notification?.permission === 'granted'
  }

  onMount() {
    this.on('keydown', (event) => this.onKey(event))
    this.subscribe('live:change', (change) => this.changed(change))
    this.subscribe('live:mail', (mail) => this.arrived(mail))
    this.setInterval(() => this.loadCounts(), 60000)
    this.load()
  }

  onGlobalStateChange(key) {
    if (key === 'mailboxes') this.load()
  }

  onRefresh(props, changed) {
    if (('container' in changed || 'mailbox' in changed) && !this.state.loading) this.loadSorting()
    this.state.navOpen = false
    if (!this.state.loading) this.land()
  }

  onDestroy() {
    document.title = 'MailCog'
  }

  async load() {
    try {
      const refs = [...new Set((this.globalState.mailboxes || []).map((mailbox) => mailbox.container))]
      const suspended = []
      const loaded = await Promise.all(
        refs.map((ref) =>
          window.App.api(`container/${ref}/my_mailboxes`, {}, { silent: true }).then(
            (data) => ({ ref, ...data }),
            (error) => {
              if (error.code === 'suspended') suspended.push(ref)
              return null
            },
          ),
        ),
      )
      this.state.suspended = suspended
      this.state.containers = loaded.filter((container) => container && container.mailboxes.length)
      this.land()
      this.loadSorting()
      await this.loadCounts()
    } catch (error) {
      this.state.error = error.message
    } finally {
      this.state.loading = false
    }
  }

  mailboxCount() {
    return this.state.containers.reduce((sum, container) => sum + container.mailboxes.length, 0)
  }

  // A container without a mailbox opens its first one; no refs is every mailbox.
  land() {
    if (!this.props.container || this.props.mailbox) return
    const container = this.state.containers.find((item) => item.ref === this.props.container) || this.state.containers[0]
    if (!container) return
    const { folder, addr, q } = this.props
    window.location.replace(routeHref('inbox', { container: container.ref, mailbox: container.mailboxes[0].ref, folder, addr, q }))
  }

  // The one mailbox in view: the filter when it names a loaded mailbox, else the only one I read.
  scope() {
    const { container, mailbox } = this.props
    if (this.state.containers.find((item) => item.ref === container)?.mailboxes.some((item) => item.ref === mailbox)) return { container, mailbox }
    const only = this.state.containers[0]
    if (!container && this.mailboxCount() === 1) return { container: only.ref, mailbox: only.mailboxes[0].ref }
    return {}
  }

  // The labels of the mailbox in view, for the sidebar, the list and the reader.
  loadLabels() {
    return this.loadSorted('labels')
  }

  // The labels, sources and forwarding addresses of the mailbox in view.
  loadSorting() {
    this.loadSorted('labels')
    this.loadSorted('sources')
    this.loadSorted('forwards')
  }

  async loadSorted(kind) {
    const { container, mailbox } = this.scope()
    if (!mailbox) {
      this.state[kind] = []
      return
    }
    try {
      const list = await window.App.api(`container/${container}/${kind}`, { mailbox }, { silent: true })
      if (mailbox === this.scope().mailbox) this.state[kind] = list.items
    } catch {
      this.state[kind] = []
    }
  }

  async manageSources(view) {
    const { container, mailbox } = this.scope()
    this.closeNav()
    await window.Dialog.open('mail-sources-dialog', { container, mailbox, addresses: this.state.addresses, can_edit: this.state.box?.can_send !== false, view: view || 'sources' }, { title: 'Other accounts', size: 'lg' })
    this.loadSorted('sources')
    this.loadSorted('forwards')
  }

  // The counts of my containers the change touched; the list, the open conversation and the
  // sorting when the view shows one of them.
  changed(change) {
    const refs = touched(change, this.state.containers.map((container) => container.ref))
    if (!refs.length) return
    this.loadCounts(refs)
    const { container } = this.scope()
    const open = refs.includes(container)
    if (open) this.loadSorting()
    if (open || !container) this.state.tick++
  }

  async loadCounts(refs) {
    const list = refs || this.state.containers.map((container) => container.ref)
    const results = await Promise.all(list.map((ref) => window.App.api(`container/${ref}/counts`, {}, { silent: true }).catch(() => null)))
    const counts = { ...this.state.counts }
    for (const result of results) for (const item of result?.items || []) counts[item.mailbox] = item
    this.state.counts = counts
    const unread = Object.values(counts).reduce((sum, item) => sum + item.inbox, 0)
    document.title = unread ? `(${unread}) MailCog` : 'MailCog'
  }

  // New Inbox mail while the tab is in the background gathers per mailbox, then notifies.
  arrived(mail) {
    if (!this.notifying()) return
    const burst = this.state.bursts[mail.mailbox]
    if (burst) return burst.push(mail)
    this.state.bursts[mail.mailbox] = [mail]
    this.setTimeout(() => this.notifyBurst(mail.container, mail.mailbox), BURST_MS)
  }

  notifying() {
    return this.state.notify && document.hidden && window.Notification?.permission === 'granted'
  }

  notifyBurst(container, mailbox) {
    const mails = this.state.bursts[mailbox] || []
    delete this.state.bursts[mailbox]
    const box = this.state.containers.find((item) => item.ref === container)?.mailboxes.find((item) => item.ref === mailbox)
    if (!box || !this.notifying()) return
    for (const notice of mailNotices(mails, box.display_name || mailboxName(box))) {
      const note = new Notification(notice.title, { body: notice.body, tag: notice.tag, renotify: true })
      note.onclick = () => {
        window.focus()
        window.App.go(routeHref('inbox', { container, mailbox, thread: notice.thread }))
        note.close()
      }
    }
  }

  async toggleNotify() {
    if (this.state.notify) {
      Fez.localStorage.set('mail-notify', false)
      this.state.notify = false
      return
    }
    if (!window.Notification) return window.Toast.info('This browser cannot show notifications.')
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return window.Toast.info('Notifications are blocked for this site in the browser settings.')
    Fez.localStorage.set('mail-notify', true)
    this.state.notify = true
    window.Toast.success('You get a notification when new mail arrives while MailCog is in the background.')
  }

  setMode(mode) {
    Fez.localStorage.set('mail-mode', mode)
    this.state.mode = mode
    this.closeReader()
  }

  openNav() {
    this.state.navOpen = true
  }

  closeNav() {
    this.state.navOpen = false
  }

  closeReader() {
    if (this.state.open) window.App.go(this.state.listHref)
  }

  // The mailbox in view, else the first one.
  home() {
    const scope = this.scope()
    if (scope.mailbox) return scope
    const first = this.state.containers[0]
    return { container: first?.ref, mailbox: first?.mailboxes[0]?.ref }
  }

  compose() {
    window.ComposeDock?.open(this.home())
  }

  showKeys() {
    window.Dialog.open('mail-keys-dialog', {}, { title: 'Keyboard shortcuts' })
  }

  onKey(event) {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return
    if (event.target.closest?.('input, textarea, select, [contenteditable]') || window.Dialog?.isOpen()) return
    const key = event.key
    if (this.state.chord) {
      this.state.chord = false
      const folder = JUMPS[key]
      if (folder) {
        event.preventDefault()
        const { container, mailbox, host, addr, source } = this.props
        window.App.go(routeHref('inbox', { container, mailbox, folder: folder === 'inbox' ? '' : folder, unread: this.props.unread ? '1' : '', host, addr, source }))
      }
      return
    }
    const list = this.find('.mail-list-fez')?.fez
    const reader = this.find('.mail-reader-fez')?.fez
    const actions = {
      j: () => list?.step(1),
      k: () => list?.step(-1),
      o: () => list?.step(0),
      Enter: () => list?.step(0),
      u: () => this.closeReader(),
      Escape: () => (this.state.navOpen ? this.closeNav() : list?.hasSelection() ? list.clearSelection() : this.closeReader()),
      x: () => list?.toggleActive(),
      e: () => (list?.hasSelection() ? list.bulk('archive') : reader?.moveTo('archive')),
      '#': () => (list?.hasSelection() ? list.bulk('trash') : reader?.moveTo('trash')),
      s: () => reader?.toggleStar(),
      U: () => reader?.markUnread(),
      r: () => reader?.respond('reply'),
      a: () => reader?.respond('all'),
      f: () => reader?.respond('forward'),
      p: () => reader?.print(),
      c: () => this.compose(),
      '/': () => list?.focusSearch(),
      '?': () => this.showKeys(),
      g: () => {
        this.state.chord = true
        this.setTimeout(() => (this.state.chord = false), 1200)
      },
    }
    if (!actions[key]) return
    event.preventDefault()
    actions[key]()
  }

  beforeRender() {
    const scope = this.scope()
    const current = this.state.containers.find((item) => item.ref === scope.container) || null
    const box = current?.mailboxes.find((item) => item.ref === scope.mailbox) || null
    this.state.scope = scope
    this.state.current = current
    this.state.box = box
    this.state.missing = Boolean(this.props.mailbox) && !box
    this.state.addresses = box ? mailboxAddresses(box, current.domains) : []
    const viewed = box ? [{ ...current, mailboxes: [box] }] : this.state.containers
    this.state.boxes = viewed.flatMap((item) =>
      item.mailboxes.map((one) => ({ container: item.ref, mailbox: one.ref, name: one.display_name || mailboxName(one), can_send: one.can_send !== false })),
    )
    // Every address of the mailboxes in view: list rows name them `me`.
    this.state.own = viewed.flatMap((item) => item.mailboxes.flatMap((one) => mailboxAddresses(one, item.domains)))
    this.state.folderName = FOLDERS.includes(this.props.folder) ? this.props.folder : 'inbox'
    this.state.open = this.props.thread || this.props.email || ''
    this.state.canAdmin = current?.role === 'admin'
    // Deleting for good: container admins, and a mailbox key with an operator's rights over its mail.
    this.state.canDelete = this.state.canAdmin || Boolean(this.globalState.mailboxSession)
    // The open conversation's mailbox: the row's `box` when no mailbox is in view.
    const readMailbox = this.props.box || scope.mailbox || ''
    const reading = this.state.containers.find((item) => item.mailboxes.some((one) => one.ref === readMailbox)) || null
    const readBox = reading?.mailboxes.find((one) => one.ref === readMailbox)
    this.state.readContainer = reading?.ref || ''
    this.state.readMailbox = readMailbox
    this.state.readAddresses = readBox ? mailboxAddresses(readBox, reading.domains) : []
    this.state.readSenders = reading ? { mailboxes: reading.mailboxes, domains: reading.domains.map((name) => ({ name })) } : null
    this.state.readCanDelete = reading?.role === 'admin' || Boolean(this.globalState.mailboxSession)
    // Labels and sources belong to the mailbox in view; another mailbox's conversation has none here.
    const inView = readMailbox === scope.mailbox
    this.state.readLabels = inView ? this.state.labels : []
    this.state.readSources = inView ? this.state.sources : []
    const folder = this.state.folderName === 'inbox' ? '' : this.state.folderName
    const { addr, host, label, source, q } = this.props
    this.state.listHref = routeHref('inbox', { container: scope.container, mailbox: scope.mailbox, folder, unread: this.props.unread ? '1' : '', host, addr, label, source, q })
    this.state.empty = !this.state.loading && !this.state.containers.length
    this.state.emptyText = window.App.isOperator()
      ? 'Mailboxes you only operate stay in Admin. Add yourself as a member of a mailbox (its container, Mailboxes) to read it here.'
      : 'Ask an operator to add you to a mailbox.'
    // The open mailbox's account is suspended, or every account this person reads is.
    this.state.frozen = this.state.suspended.includes(this.props.container) || (this.state.empty && this.state.suspended.length > 0)
  }

  CSS = `:fez {
display:block;height:100%;.inbox-note{display:grid;place-items:center;height:100%;padding:var(--space-6)}.inbox{display:grid;grid-template-columns:var(--sidebar-width) minmax(320px,var(--thread-width)) minmax(0,1fr);height:100%;min-height:0}.inbox-nav{min-height:0;overflow-y:auto;border-right:1px solid var(--color-border);background:var(--color-bg-subtle)}.inbox-list{display:flex;flex-direction:column;min-width:0;min-height:0;border-right:1px solid var(--color-border);background:var(--color-bg-raised)}.inbox-reader{min-width:0;min-height:0;overflow-y:auto;background:var(--color-bg)}.inbox-pane{min-height:100%}.inbox-idle{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:var(--space-2);height:100%;color:var(--color-text-subtle);text-align:center}.inbox-idle-title{margin-top:var(--space-2);color:var(--color-text-muted);font-weight:var(--weight-medium)}.inbox-idle-hint{display:flex;align-items:center;gap:var(--space-2);font-size:var(--text-sm)}.inbox-scrim{display:none}@media (max-width:1099px){.inbox{grid-template-columns:minmax(300px,360px) minmax(0,1fr)}.inbox-nav{position:fixed;top:0;bottom:0;left:var(--rail-width);z-index:var(--z-dropdown);width:var(--sidebar-width);box-shadow:var(--shadow-lg);transform:translateX(calc(-100% - var(--rail-width)));visibility:hidden;transition:transform var(--duration-normal) var(--ease),visibility var(--duration-normal)}.inbox-nav-open .inbox-nav{transform:none;visibility:visible}.inbox-nav-open .inbox-scrim{display:block;position:fixed;inset:0 0 0 var(--rail-width);z-index:calc(var(--z-dropdown) - 1);padding:0;border:0;background:var(--color-overlay);cursor:default}} :mobile {.inbox{grid-template-columns:minmax(0,1fr)}.inbox-reading .inbox-list,.inbox:not(.inbox-reading) .inbox-reader{display:none}.inbox-list{border-right:0}.inbox-nav{left:0;bottom:calc(40px + 2 * var(--space-2));width:min(var(--sidebar-width),86vw);transform:translateX(-100%)}.inbox-nav-open .inbox-scrim{inset:0 0 calc(40px + 2 * var(--space-2)) 0}}
}`

  HTML = `{#if state.error}
<div class="inbox-note"><ui-empty icon="warning-circle" title="Could not load your mail" text={state.error}></ui-empty></div>
{:else if state.loading}
<div class="inbox-note"><ui-spinner size="lg"></ui-spinner></div>
{:else if state.frozen}
<div class="inbox-note"><ui-empty icon="snowflake" title="This mail account is suspended" text="Its mail is on hold for now: nothing comes in or goes out, and it cannot be opened. Contact the service administrator."></ui-empty></div>
{:else if state.empty}
<div class="inbox-note"><ui-empty icon="tray" title="No mailboxes yet" text={state.emptyText}></ui-empty></div>
{:else if state.missing}
<div class="inbox-note"><ui-empty icon="tray" title="Mailbox not found" text="It was removed, or you no longer read it."></ui-empty></div>
{:else}
<div class="inbox" class:inbox-reading={state.open} class:inbox-nav-open={state.navOpen}>
<aside class="inbox-nav" aria-label="Mailboxes">
<mail-sidebar :containers="state.containers" container={state.scope.container || ''} mailbox={state.scope.mailbox || ''} folder={state.folderName} addr={props.addr || ''} host={props.host || ''} :unread="props.unread" :counts="state.counts" :labels="state.labels" label={props.label || ''} :sources="state.sources" source={props.source || ''} :forwards="state.forwards" :notify="state.notify" :on_compose="() => fez.compose()" :on_notify="() => fez.toggleNotify()" :on_sources="(view) => fez.manageSources(view)"></mail-sidebar>
</aside>
<button class="inbox-scrim" type="button" aria-label="Close menu" onclick="fez.closeNav()"></button>
<section class="inbox-list">
<mail-list container={state.scope.container || ''} mailbox={state.scope.mailbox || ''} :boxes="state.boxes" :unread="props.unread" host={props.host || ''} folder={state.folderName} addr={props.addr || ''} label={props.label || ''} :labels="state.labels" source={props.source || ''} :sources="state.sources" q={props.q || ''} mode={state.mode} :open="state.open" :tick="state.tick" :addresses="state.addresses" :own="state.own" :can_delete="state.canDelete" :on_menu="() => fez.openNav()" :on_mode="(mode) => fez.setMode(mode)"></mail-list>
</section>
<section class="inbox-reader">
{#if state.open}
<div class="inbox-pane">
<mail-reader container={state.readContainer} mailbox={state.readMailbox} thread={props.thread || ''} email={props.email || ''} back_href={state.listHref} :tick="state.tick" :senders="state.readSenders" :addresses="state.readAddresses" :can_delete="state.readCanDelete" :labels="state.readLabels" :sources="state.readSources" :on_labels="() => fez.loadLabels()"></mail-reader>
</div>
{:else}
<div class="inbox-idle">
<ui-icon name="envelope-open" size="44"></ui-icon>
<p class="inbox-idle-title">{state.mode === 'threads' ? 'Pick a conversation to read it' : 'Pick a message to read it'}</p>
<p class="inbox-idle-hint">Press <ui-kbd keys="?"></ui-kbd> for keyboard shortcuts</p>
</div>
{/if}
</section>
</div>
<mail-compose-dock></mail-compose-dock>
{/if}`
};
}

Fez('tpl-inbox', __fez_component_0);

export default __fez_component_0;
